
// src/lib/payment.ts
import { toast } from "@/components/ui/use-toast";
import { auth, db, updateUserSubscriptionStatus } from "@/lib/firebase";
import { doc, updateDoc, getDoc, Timestamp, increment } from "firebase/firestore";

// Stripe public (publishable) key from environment variable
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
export const PRICE_ID_BASIC = "price_1RKFnmH86kqUmtakf2mF1PYm";  // 30 scans - $1.99/month
export const PRICE_ID_PRO   = "price_1RKFqeH86kqUmtakQDDY0r3R";  // Unlimited - $8.99/month

// Call same-origin API; Hosting rewrites /api/* → Cloud Run
export const API_BASE = "https://api-f2cz2uwarq-uc.a.run.app";

// ── Stripe singleton ────────────────────────────────────────
let stripePromise: Promise<any> | null = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = import("@stripe/stripe-js").then(({ loadStripe }) =>
      loadStripe(STRIPE_PUBLIC_KEY)
    );
  }
  return stripePromise;
};

// ── Create Checkout Session & redirect ──────────────────────
export const initializeSubscription = async (tier: 'basic' | 'pro' = 'basic'): Promise<boolean> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      toast({
        title: "Not logged in",
        description: "Please sign in to subscribe.",
        variant: "destructive"
      });
      return false;
    }

    // ← UPDATED: include the endpoint path
    const res = await fetch(`${API_BASE}/api/createCheckoutSession`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${await user.getIdToken()}`
      },
      body: JSON.stringify({
        priceId: tier === 'basic' ? PRICE_ID_BASIC : PRICE_ID_PRO,
        tier: tier,
        origin: window.location.origin
      })
    });

    if (!res.ok) {
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        throw new Error(data.error || "Server error");
      } catch {
        console.error("Invalid JSON response:", text);
        throw new Error(`Server error: ${res.status} ${res.statusText}`);
      }
    }
    
    const data = await res.json();
    const stripe = await getStripe();
    if (!stripe) throw new Error("Stripe failed to initialize");
    
    const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
    if (error) throw new Error(error.message);

    return true;
  } catch (err: any) {
    console.error("initializeSubscription:", err);
    toast({
      title: "Subscription Error",
      description: err.message || "There was an error processing your subscription.",
      variant: "destructive"
    });
    return false;
  }
};

// ── Helpers: scan balance & decrement ───────────────────────
export const hasScansRemaining = async (): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;

  const snap = await getDoc(doc(db, "users", user.uid));
  if (!snap.exists()) return false;

  if (snap.data().subscribed && snap.data().subscriptionTier === 'pro') {
    return true;  // Pro users always have scans available
  }
  
  return (snap.data().remainingScans ?? 0) > 0;
};

export const decrementScan = async () => {
  const user = auth.currentUser;
  if (!user) return;

  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  
  if (snap.exists()) {
    const userData = snap.data();
    if (!userData.subscribed || userData.subscriptionTier !== 'pro') {
      const curr = userData.remainingScans ?? 0;
      await updateDoc(ref, {
        remainingScans: Math.max(0, curr - 1),
        updatedAt: Timestamp.now()
      });
    }
  }
};

// ── Handle Stripe redirect params ───────────────────────────
export const checkSubscriptionRedirect = async (): Promise<boolean> => {
  try {
    console.log("Checking subscription redirect...");
    const qp = new URLSearchParams(window.location.search);
    const status = qp.get("subscription");
    const tierParam = qp.get("tier") || "basic"; // Get tier from URL if available
    
    console.log("Subscription status:", status, "Tier:", tierParam);

    if (status === "success") {
      const user = auth.currentUser;
      console.log("User authenticated:", !!user);
      
      if (user) {
        // Update subscription through our centralized function
        console.log(`Updating user subscription to ${tierParam} tier`);
        await updateUserSubscriptionStatus(user.uid, tierParam as 'basic' | 'pro');
        
        toast({
          title: "Subscription Successful!",
          description: "Thank you for subscribing! Your account has been updated.",
          className: "success-toast"
        });
        
        // Clean up query params but keep success state for celebration
        qp.delete("tier");
        window.history.replaceState(
          {},
          document.title, 
          window.location.pathname + "?subscription=success"
        );
        return true;
      } else {
        toast({
          title: "Subscription Successful!",
          description: "Thank you! Please log in to access your subscription.",
        });
      }
      
      window.history.replaceState(
        {},
        document.title, 
        window.location.pathname + "?subscription=success"
      );
      return true;
    } else if (status === "canceled") {
      toast({
        title: "Subscription Canceled",
        variant: "destructive"
      });
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error in checkSubscriptionRedirect:", error);
    return false;
  }
};
